import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18n-js';

import clsx from 'clsx';
import { Table } from 'components/UI';
import styled from 'styled-components';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import CardContent from '@material-ui/core/CardContent';
import LinearProgress from '@material-ui/core/LinearProgress';

import { customStyles, useStyles } from './Lists.styled';

const LinearIndeterminate = () => {
  return (
    <div style={{ width: '100%', marginBottom: 40 }}>
      <LinearProgress />
    </div>
  );
};

const NoDataComponent = () => {
  return (
    <div
      style={{
        padding: '24px',
        textAlign: 'center',
        color: '#4D4F5C',
        fontSize: 13,
      }}
    >
      {i18n.t('common.no_records_to_display')}
    </div>
  );
};

const DataTable = styled(Table)`
  .rdt_Pagination > span:nth-child(3) {
    display: none;
  }
`;

const SelectableList = ({
  title,
  columns,
  data,
  loading,
  totalRows,
  selectableField,
  handlePageChange,
  handlePerRowsChange,
  pagination,
  dataCy,
  onRowClicked,
  disabledField,
  disableSelectable,
  mainTable,
  enabledHeaderColor,
  field = 'id',
  paginationDefaultPage,
  paginationPerPage,
}) => {
  const [dataList, setDataList] = useState([]);

  useEffect(() => {
    setDataList([...data]);
  }, [data]);

  const classes = useStyles();

  /**
   * Conditional styles for rows
   * When row is selected or row is disabled
   */
  const conditionalRowStyles = [
    {
      when: (row) => row[selectableField],
      style: {
        backgroundColor: '#178BFF',
        color: '#FFF',
        '&:hover': {
          backgroundColor: '#178BFF',
          color: '#FFF',
          cursor: 'pointer',
        },
      },
    },
    {
      when: (row) => row[disabledField],
      style: {
        backgroundColor: '#FBFBFB',
        cursor: 'not-allowed',
        '&:hover': {
          backgroundColor: '#FBFBFB',
        },
      },
    },
  ];

  /**
   * Handle row clicked method
   * @param {object} row Object with selected row data
   */
  const handleRowClicked = (row) => {
    if (row[disabledField] || disableSelectable) {
      return;
    }
    const rowClicked = dataList.findIndex((val) => val[field] === row[field]);
    const prevRowClicked = dataList.findIndex((val) => val.selected);

    const _data = [...dataList];
    if (rowClicked === prevRowClicked) {
      if (!_data[rowClicked].selected) {
        onRowClicked(_data[rowClicked]);
      } else {
        onRowClicked(null);
      }
      _data[rowClicked].selected = !_data[rowClicked].selected;
    } else {
      if (prevRowClicked !== -1) {
        _data[prevRowClicked].selected = false;
      }
      _data[rowClicked].selected = true;
      onRowClicked(_data[rowClicked]);
    }

    setDataList(_data);
  };

  let styles = {};

  if (enabledHeaderColor) {
    styles = {
      background: data.length ? '#0181FF' : '#777',
    };
  }

  return (
    <Card>
      <CardHeader
        title={title}
        style={styles}
        className={clsx(
          classes.headerRoot,
          { [classes.headerFilled]: !mainTable && dataList?.length > 0 },
          { [classes.headerEmpty]: !mainTable && dataList?.length === 0 }
        )}
      />
      <CardContent data-cy={dataCy} className={classes.content}>
        {dataList?.length > 0 ? (
          <div className="tableSelectableList">
            <DataTable
              data={dataList}
              noHeader={true}
              persistTableHead
              highlightOnHover
              paginationServer
              columns={columns}
              pagination={pagination}
              progressPending={loading}
              customStyles={customStyles}
              onRowClicked={handleRowClicked}
              paginationTotalRows={totalRows}
              onPageChange={handlePageChange}
              noDataComponent={<NoDataComponent />}
              onRowsPerPageChange={handlePerRowsChange}
              conditionalRowStyles={conditionalRowStyles}
              progressComponent={<LinearIndeterminate />}
              paginationDefaultPage={paginationDefaultPage}
              paginationPerPage={paginationPerPage}
            />
          </div>
        ) : (
          <NoDataComponent />
        )}
      </CardContent>
    </Card>
  );
};

SelectableList.propTypes = {
  data: PropTypes.array,
  loading: PropTypes.bool,
  title: PropTypes.string,
  dataCy: PropTypes.string,
  columns: PropTypes.array,
  mainTable: PropTypes.bool,
  pagination: PropTypes.bool,
  totalRows: PropTypes.number,
  disabledField: PropTypes.string,
  handlePageChange: PropTypes.func,
  disableSelectable: PropTypes.bool,
  enabledHeaderColor: PropTypes.bool,
  selectableField: PropTypes.string,
  handlePerRowsChange: PropTypes.func,
};

SelectableList.defaultProps = {
  data: [],
  title: '',
  columns: [],
  totalRows: 0,
  dataCy: 'list',
  loading: false,
  mainTable: false,
  pagination: true,
  disableSelectable: false,
  enabledHeaderColor: false,
  disabledField: 'disabled',
  handlePageChange: () => {},
  selectableField: 'selected',
  handlePerRowsChange: () => {},
};

export default SelectableList;
